import React from 'react';
import PropTypes from 'prop-types';
import { useLanguage } from '@contexts/LanguageContext';
import Icon from '@components/Icon/Icon';
import { faAsterisk, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { FIELD_TYPE_ICONS, CONTEXT_ICONS } from './constants';

/**
 * Component for displaying a field in read-only mode
 */
const FieldItemDisplay = ({
  fieldKey,
  fieldDef,
  onEdit,
  onDelete,
  disabled = false,
  onDragStart,
  onDragEnd
}) => {
  const { t } = useLanguage();

  return (
    <>
      <div className="field-header">
        {!disabled && (
          <div
            className="drag-handle"
            title={t('common.dragToReorder') || 'Glisser pour réorganiser'}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <Icon icon="grip-vertical" />
          </div>
        )}
        <div className="field-title">
          <strong>{fieldDef.label}</strong>
          <span className="field-key">({fieldKey})</span>
          <span className={`field-type badge-type ${fieldDef.type}`}>
            <Icon icon={FIELD_TYPE_ICONS[fieldDef.type]} />
            {t(`borrowingFields.fieldTypes.${fieldDef.type}`) || fieldDef.type}
          </span>
          {fieldDef.required && (
            <span className="badge badge-required">
              <Icon icon={faAsterisk} />
              {t('common.required') || 'Obligatoire'}
            </span>
          )}
          {fieldDef.description_field && (
            <span className="badge badge-description">
              <Icon icon={faFileAlt} />
            </span>
          )}
        </div>
        <div className="field-actions">
          <button
            type="button"
            onClick={onEdit}
            disabled={disabled}
            className="btn-edit"
            title={t('common.edit') || 'Edit'}
          >
            <Icon icon="edit" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={disabled}
            className="btn-delete"
            title={t('common.delete') || 'Delete'}
          >
            <Icon icon="trash" />
          </button>
        </div>
      </div>

      {fieldDef.help_text && (
        <p className="field-help">
          <Icon icon="question-circle" /> {fieldDef.help_text}
        </p>
      )}

      <div className="field-contexts">
        {(fieldDef.contexts || []).map(context => (
          <span key={context} className={`badge badge-context badge-context-${context}`}>
            <Icon icon={CONTEXT_ICONS[context]} />
            {t(`borrowingFields.contexts.${context}`) || context}
          </span>
        ))}
        {(!fieldDef.contexts || fieldDef.contexts.length === 0) && (
          <span className="text-muted">
            {t('borrowingFields.manager.allContexts') || 'Tous'}
          </span>
        )}
      </div>

      {fieldDef.type === 'select' && fieldDef.options && (
        <div className="field-options">
          <strong>
            <Icon icon="list" /> {t('borrowingFields.manager.options') || 'Options'}:
          </strong>
          {fieldDef.options.map((option, idx) => (
            <span key={idx} className="badge badge-option">{option}</span>
          ))}
        </div>
      )}
    </>
  );
};

FieldItemDisplay.propTypes = {
  fieldKey: PropTypes.string.isRequired,
  fieldDef: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onDragStart: PropTypes.func,
  onDragEnd: PropTypes.func
};

export default FieldItemDisplay;
